import { EventBus } from "./events";
import { mulberry32, type Rng } from "./rng";
import { BugManager } from "./bugs";
import { createBlocks, type BlocksModel } from "./blocks";
import { SpawnerManager } from "./spawners";
import { PowerUpManager, type PowerUpId } from "./powerups";
import { createModifiers, type GlobalModifiers } from "./modifiers";
import { ComboTracker } from "./combos";
import { CommandQueue, type Command } from "./commands";
import { LevelClock } from "./levels";
import { DIFFICULTIES, LEVELS, LOSE_HEALTHY_FRACTION, type DifficultyDef, type LevelDef } from "./defs";
import { FACE_IDS, N, type Dir4, type SurfacePose } from "./surface";
import { restingTile, type WalkerId } from "./occupancy";

/** Fixed simulation timestep in seconds (00-architecture.md §2). */
export const SIM_DT = 1 / 60;

/** Container for all simulation state. Systems are added by later plans. */
export interface World {
  readonly events: EventBus;
  readonly rng: Rng;
  /** The cube itself. Bugs read it to avoid holes and to eat what they walk on. */
  readonly blocks: BlocksModel;
  readonly bugs: BugManager;
  /** The apples. Empty until the composition root places them. */
  readonly spawners: SpawnerManager;
  /** The plants: spawn timer, lifecycle, and the ten effects (plan 16). */
  readonly powerups: PowerUpManager;
  /** The global fields power-up effects write and bugs/spawners read. */
  readonly modifiers: GlobalModifiers;
  /** Repair chains; fed by successful repairs, emits `combo` and `scored`. */
  readonly combos: ComboTracker;
  /** What the player asked for; drained at the top of the next step. */
  readonly commands: CommandQueue;
  /** The difficulty ramp: which level it is, and the row that governs it. */
  readonly levels: LevelClock;
  /** Accumulated simulation seconds. */
  time: number;
  /** Whether `gameLost` has already fired; it fires exactly once per run. */
  lostEmitted: boolean;
}

/**
 * What kind of run this world hosts (plan 17): the menu's unattended demo
 * passes `DEMO_LEVELS`, a real game passes the picked difficulty. Both default
 * to the pre-17 behaviour (main table, easy) so every existing test and debug
 * scenario builds unchanged.
 */
export interface WorldOptions {
  readonly levels?: readonly LevelDef[];
  readonly difficulty?: DifficultyDef;
}

export function createWorld(seed: number, opts: WorldOptions = {}): World {
  const events = new EventBus();
  const rng = mulberry32(seed);
  const blocks = createBlocks(N, events);
  const levels = new LevelClock(events, opts.levels ?? LEVELS, opts.difficulty ?? DIFFICULTIES[0]);
  const modifiers = createModifiers();
  const bugs = new BugManager(events, blocks, rng, levels, modifiers);
  const powerups = new PowerUpManager(events, rng, blocks, bugs, levels, modifiers);
  // Late-bound: the kill-half effect needs `bugs`, so the constructor order
  // cannot satisfy both directions (see `BugManager.powerups`).
  bugs.powerups = powerups;
  return {
    events,
    rng,
    blocks,
    bugs,
    spawners: new SpawnerManager(events, rng, bugs, bugs.occupancy, levels, modifiers),
    powerups,
    modifiers,
    combos: new ComboTracker(events),
    commands: new CommandQueue(),
    levels,
    time: 0,
    lostEmitted: false,
  };
}

/** How many random draws a relocation gets before it gives up for this step. */
const RELOCATE_ATTEMPTS = 32;

/**
 * A free lane centre for a newly added apple.
 *
 * APPROX: the original adds a spawn point by *relocating* one
 * (`RelocateSpawnPoint(null)`), which walks its own placement machinery; a
 * rejection-sampled tile centre drawn from the sim's seeded RNG is this port's
 * version. `null` when every draw landed on a claimed or non-walkable tile,
 * in which case the reconciliation simply retries at the next level.
 */
function freeLaneCentre(world: World): SurfacePose | null {
  for (let i = 0; i < RELOCATE_ATTEMPTS; i++) {
    const pose: SurfacePose = {
      face: FACE_IDS[Math.floor(world.rng() * FACE_IDS.length)],
      u: Math.floor(world.rng() * N) + 0.5,
      v: Math.floor(world.rng() * N) + 0.5,
      dir: Math.floor(world.rng() * 4) as Dir4,
    };
    const tiles = restingTile(pose);
    if (tiles.some((key) => world.bugs.occupancy.holder(key) !== null)) continue;
    return pose;
  }
  return null;
}

/**
 * Match the apple population to the new level's `AmountOfSpawnPoints` — the
 * spawn-point half of `BugsModel._cubeView_LevelChanged` (`:42-54`). Removal
 * takes the **oldest** (`RemoveSpawnPoint(_spawnPoints[0])`); the map preserves
 * insertion order, so its first key is that apple.
 *
 * The column is 2 for all 30 shipped levels, so this loop never runs in the
 * shipped game. It is ported anyway: a dead branch that only wakes when someone
 * edits the table should already work when they do.
 */
function reconcileSpawnPoints(world: World, def: LevelDef): void {
  const spawners = world.spawners;
  while (spawners.spawners.size > def.spawnPoints) {
    const oldest = spawners.spawners.keys().next();
    if (oldest.done === true) return;
    spawners.remove(oldest.value);
  }
  while (spawners.spawners.size < def.spawnPoints) {
    const pose = freeLaneCentre(world);
    if (pose === null) return;
    spawners.add(pose);
  }
}

/**
 * One queued player command (slot 1 of the step order). A tap on a bug hurts
 * it; a repair is refused while any walker stands on the target block — the
 * original's `GetCubeWalkerBlockingPlaneRepair` gate in `BlocksModel.RepairBlock`,
 * which iterates power-ups too (`BugsModel.cs:301-313`) — and only a repair
 * that actually changed something feeds the combo chain, matching
 * `RepairBlock`'s "returns true if it was not already healthy".
 */
function applyCommand(world: World, command: Command): void {
  switch (command.kind) {
    case "tapBug":
      world.bugs.hurt(command.id);
      return;
    case "repairBlock": {
      const cell = command.cell;
      const blockers: (WalkerId | PowerUpId)[] = world.bugs.occupancy.holdersByCell(cell);
      const powerUp = world.powerups.at(cell);
      if (powerUp !== null) blockers.push(powerUp.id);
      if (blockers.length > 0) {
        world.events.emit("repairBlocked", { cell, blockers });
        return;
      }
      if (world.blocks.repair(cell[0], cell[1], cell[2])) world.combos.addRepair(cell, world.time);
      return;
    }
    case "tapPowerUp":
      world.powerups.take(command.id);
      return;
    default: {
      const unreachable: never = command;
      throw new Error(`unhandled command ${JSON.stringify(unreachable)}`);
    }
  }
}

/** Advance the simulation by exactly one `SIM_DT`. */
export function stepWorld(world: World): void {
  // Fixed system update order (00-architecture.md §2). Systems land here as
  // later plans implement them; the order is binding:
  //   0. the level clock
  //   1. queued player commands
  //   2. spawners
  //   3. power-ups
  //   4. bug movement
  //   5. collision
  //   6. infection
  //   7. block model reactions (and the lose check)
  //   8. event flush
  //
  // Slot 0 is first so a level change is in force for the whole step that
  // notices it — the weights a spawner rolls and the speed a walker carries
  // must not be half a step out of date. Slot 3 (plan 16) sits before the
  // walkers so a power-up is placed before they move: a bug stepping onto the
  // cell in the same step tramples it through the tile-entry hook rather than
  // ending up standing on one it never entered.
  const levelBefore = world.levels.level;
  world.levels.update(world.time);
  if (world.levels.level !== levelBefore) reconcileSpawnPoints(world, world.levels.current);
  // Commands queued during level 0 are **dropped**, not deferred — the original
  // returns early out of `HandleInput` (`Cube.cs:3190`) rather than buffering.
  // It is a one-second window; the point is that the rule lives in the sim.
  const queued = world.commands.drain();
  if (world.levels.acceptsInput) for (const command of queued) applyCommand(world, command); // slot 1
  world.spawners.update(SIM_DT); // slot 2: spawners
  world.powerups.update(SIM_DT); // slot 3: power-ups — placed before walkers move
  // Slots 4–6 are one pass: a walker's step, the tiles it may enter and the
  // bite it takes are the same state machine (`updateBug`), and splitting them
  // would mean walking the bug map three times to no benefit. Slot 7 needs no
  // pass of its own — `BlocksModel` reacts inside `infect()` and emits there.
  world.bugs.update(SIM_DT); // slots 4-6: bug movement, collision, infection
  // Slot 7: the block model's own reaction — infected blocks decaying to ghost
  // on the `INFECTED_SECONDS` countdown (`BlocksModel.Update`). It runs after
  // the bugs so a block bitten this step gets its full stage.
  world.blocks.update(SIM_DT);
  // Still slot 7: the lose check reads the block model after it has settled for
  // the step (`Cube.cs:2345`). It fires once and stops nothing — see `gameLost`.
  if (!world.lostEmitted) {
    const healthy = world.blocks.healthyFraction();
    if (healthy <= LOSE_HEALTHY_FRACTION) {
      world.lostEmitted = true;
      world.events.emit("gameLost", { healthyFraction: healthy });
    }
  }
  world.time += SIM_DT;
}

/**
 * How many fixed steps a pending time accumulator releases, and what is left
 * over. Pure so the frame loop's timing logic is testable without a browser.
 */
export function drainAccumulator(acc: number, step: number): { steps: number; rest: number } {
  if (acc < step) return { steps: 0, rest: acc };
  const steps = Math.floor(acc / step);
  return { steps, rest: acc - steps * step };
}
